import { defaultTo } from 'lodash';
import { useContrastText } from 'native-base';
import React from 'react';
import { Heading, Image, Layout, Pressable, Text } from '../../../../../components/core';
import { IconChevronRight } from '../../../../../components/icons/IconChevronRight';
import { getRoute, useNavigate } from '../../../../../routes';
import { getStylesheet } from '../../../../../styles';
import { getColorFromShade } from '../../../../../utils/color';
import { ModuleDocument } from '../../../../module/ModuleTypes';

type CollectionModuleBoxProps = {
  module: ModuleDocument;
};

export const CollectionModuleBox = ({ module }: CollectionModuleBoxProps): JSX.Element => {
  const navigate = useNavigate();
  const onCollectionModulePress = (moduleId: string) => (): void => {
    navigate(getRoute('module', { moduleId }));
  };

  const data = module.data;
  const color = defaultTo(data?.color, '');
  const colorShade = defaultTo(data?.color_shade, '');
  const imageUrl = defaultTo(data.image?.url, '');
  const imageAlt = defaultTo(data.image?.alt, 'Image');
  const title = defaultTo(data.title, 'Module Title');

  const backgroundColor = getColorFromShade(color, colorShade, 'secondary.900');

  // Determines the text color based on the background color.
  const textColor = useContrastText(backgroundColor);

  return (
    <Pressable
      {...styles.moduleBox}
      backgroundColor={backgroundColor}
      testID="module-box"
      onPress={onCollectionModulePress(module.id)}
    >
      <Layout.View {...styles.moduleBoxTop}>
        {imageUrl && (
          <Image
            {...styles.moduleThumbnail}
            alt={imageAlt}
            source={{
              uri: imageUrl,
            }}
            resizeMode="contain"
            resizeMethod="auto"
          />
        )}
      </Layout.View>

      <Layout.VStack {...styles.moduleBoxStack} space={4}>
        <Layout.VStack flexGrow={1} space={2}>
          <Heading.h4 color={textColor}>{title}</Heading.h4>

          <Layout.HStack size={2} alignItems="center">
            <Text.paraSmall bold color={textColor}>
              View collection
            </Text.paraSmall>
            <IconChevronRight color={textColor} size={4} />
          </Layout.HStack>
        </Layout.VStack>
      </Layout.VStack>
    </Pressable>
  );
};

const styles = getStylesheet({
  moduleBox: {
    borderRadius: 4,
    flex: 1,
    height: '264px',
    minWidth: '45%',
    overflow: 'hidden',
  },

  moduleBoxTop: {
    flex: 1,
    position: 'relative',
  },

  moduleBoxStack: {
    alignItems: 'flex-start',
    paddingBottom: 4,
    paddingX: 4,
  },

  moduleThumbnail: {
    position: 'absolute',
    size: '160px',
    top: '-14px',
    right: '-17px',
  },
});
